import Vue from 'vue'
import router from '@/router'
import to from 'await-to-js'
import client from '@/api/client'
import ProductDataService from '@/api/services/_shared/product-data-service.js'
import productGroupReviewTotalsQuery from '@/api/gql/product/query/product-group-review-totals.gql'
import productGroupReviewsQuery from '@/api/gql/product/query/product-group-reviews.gql'
import productGroupQuestionsQuery from '@/api/gql/product/query/product-group-questions.gql'
import createQuestionMutation from '@/api/gql/product/mutation/create-question.gql'
import reviewRequestQuery from '@/api/gql/review_request/query/review-request.gql'
import createReviewMutation from '@/api/gql/product/mutation/create-review.gql'
import {
  redirectToFilterPage,
  findVariantIdByRouteColors,
  orderVariantsByNewColors,
} from '@/stores/product-store-helpers.js'
import { loadForAtLeast } from '@/helpers/request'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import routeNames from '@/router/route-names'
import {
  FIRST_QUESTION_PAGE,
  REVIEWS_PER_PAGE,
  QUESTIONS_PER_PAGE,
  REVIEW_FALLBACK_LANGUAGE,
  REVIEWS_FALLBACK_THRESHOLD,
  GENDERS,
} from '@/stores/product.js'

const sendItem = async (operation, dataKey, queryOrMutation, variables) => {
  const queryOrMutationKey = operation === 'query' ? 'query' : 'mutation'
  const [error, result] = await to(
    client[operation]({
      [queryOrMutationKey]: queryOrMutation,
      variables,
    })
  )
  if (error) Vue.rollbar.error(error)
  return result?.data?.[dataKey]
}

const queryItem = async (dataKey, query, variables) => {
  return sendItem('query', dataKey, query, variables)
}

const mutateItem = async (dataKey, mutation, variables) => {
  return sendItem('mutate', dataKey, mutation, variables)
}

const shouldFetchFallbackReviews = ({ state }) => {
  return (
    state.reviewLanguage !== REVIEW_FALLBACK_LANGUAGE &&
    state.reviewPageIndex === 1 &&
    state.reviews.length < Math.min(REVIEWS_FALLBACK_THRESHOLD, REVIEWS_PER_PAGE)
  )
}

const fetchNextReviewPage = async ({ state, commit, getters }) => {
  commit('setIsLoadingReviews', true)
  const queryOptions = {
    query: productGroupReviewsQuery,
    variables: {
      slug: getters.productGroupSlug,
      language: state.reviewLanguage,
      reviewPage: state.reviewPageIndex + 1,
      reviewsPerPage: REVIEWS_PER_PAGE,
    },
  }
  if (state.activeSort.code) {
    queryOptions.variables.sortBy = {
      feature: state.activeSort.feature,
      order: state.activeSort.order,
    }
  }
  const [error, result] = await to(loadForAtLeast(client.query(queryOptions)))
  if (error) Vue.rollbar.error(error)
  const productGroup = result?.data?.productGroup
  if (productGroup?.reviews) {
    commit('addReviewsPage', productGroup)
  }
  commit('setIsLoadingReviews', false)
}

export default {
  replaceRoute({ rootState, state, getters }, isVariantClick = true) {
    to(
      router.replace({
        name: routeNames.product,
        params: {
          ...rootState.route.params,
          gender: state.selectedGender,
          colors: getters.selectedColors,
          isVariantClick,
        },
      })
    )
  },

  validateRoute({ rootState, getters }) {
    const { colors, gender } = rootState.route.params
    const routeColors = colors?.split('-')
    const isValidColors =
      !routeColors || !!findVariantIdByRouteColors(getters.product.variants, routeColors)
    const isValidGender = !gender || Object.values(GENDERS).includes(gender)

    if (!isValidColors || !isValidGender) {
      // Fallback to main variant which is the first male variant
      router.replace({
        name: routeNames.product,
        query: rootState.route.query,
        hash: rootState.route.hash,
        params: { isVariantClick: true },
      })
    }
  },

  handleGenderSwitch({ state, getters, commit, dispatch }, gender) {
    if (gender === state.selectedGender) return
    commit('setGender', gender)
    const currentVariantId =
      gender === GENDERS.mens ? getters.firstMaleVariantId : getters.firstFemaleVariantId
    commit('setCurrentVariantId', currentVariantId)
    commit('resetSlideIndex')
    if (!state.isRouteUpdatingBlocked) dispatch('replaceRoute')
    notifyExternalServices({
      action: ACTIONS.productInteraction,
      details: { action: 'gender-select', label: gender },
    })
  },

  async fetchData({ rootState, commit, dispatch, getters, state }, payload = {}) {
    const [error, result] = await to(ProductDataService.fetch({ slug: payload.slug }))
    if (error || !result || !result.data?.productGroup?.productData?.variants.length)
      return redirectToFilterPage(rootState.page.hostname, rootState.page.locale)

    const { productGroup, sizeInfo: { isSizeInfoVisible = false } = {} } = result.data
    commit('setProductGroup', orderVariantsByNewColors(productGroup))
    if (getters.isRaffle) dispatch('page/startUpdatingCurrentTime', null, { root: true })
    commit('setSizeInfo', isSizeInfoVisible)
    notifyExternalServices({
      action: ACTIONS.sizeInfo,
      details: { isSizeInfoVisible },
    })
    if (payload.isRouteUpdatingBlocked) commit('blockRouteUpdating')
    if (!state.isRouteUpdatingBlocked) dispatch('validateRoute')
  },

  async loadReviewTotals({ state, rootState, commit }, payload) {
    if (!state.reviewLanguage) {
      commit('setReviewLanguage', rootState.page.language)
      if (!rootState.page.language) {
        Vue.rollbar.error('rootState.page.language is not defined in loadReviewTotals', {
          pageStore: rootState.page,
        })
      }
    }
    const [error, result] = await to(
      client.query({
        query: productGroupReviewTotalsQuery,
        variables: {
          slug: payload?.slug,
          language: state.reviewLanguage,
        },
      })
    )
    if (error) Vue.rollbar.error(error)
    const productGroup = result?.data?.productGroup
    if (productGroup) {
      commit('addReviewTotals', productGroup)
    }
  },

  async loadReviews(context) {
    if (!context.state.reviewLanguage) {
      context.commit('setReviewLanguage', context.rootState.page.language)
    }
    await fetchNextReviewPage(context)
    if (shouldFetchFallbackReviews(context)) {
      context.commit('setReviewLanguage', REVIEW_FALLBACK_LANGUAGE)
      await fetchNextReviewPage(context)
    }
  },

  async setSorting({ state, commit, dispatch }, sorting) {
    if (state.activeSort.code === sorting.code) return
    commit('setActiveSort', sorting)
    commit('resetReviews')
    await dispatch('loadReviews')
  },

  async loadMoreReviews(context) {
    await fetchNextReviewPage(context)
  },

  async loadQuestions({ commit, dispatch }) {
    commit('resetQuestions')
    await dispatch('fetchQuestions', { questionPage: FIRST_QUESTION_PAGE })
  },

  async loadMoreQuestions({ state, dispatch }) {
    await dispatch('fetchQuestions', { questionPage: state.questionPageIndex + 1 })
  },

  async fetchQuestions(
    { rootState, commit, getters },
    { questionPage, language = rootState.page.language }
  ) {
    commit('setIsLoadingQuestions', true)
    const [error, result] = await to(
      loadForAtLeast(
        client.query({
          query: productGroupQuestionsQuery,
          variables: {
            slug: getters.productGroupSlug,
            language,
            questionPage,
            questionsPerPage: QUESTIONS_PER_PAGE,
          },
        })
      )
    )
    if (error) Vue.rollbar.error(error)
    const questions = result?.data?.productGroup?.questions
    if (questions) commit('addQuestionsPage', questions)
    commit('setQuestionTotal', result?.data?.productGroup?.questionTotal)
    commit('setIsLoadingQuestions', false)
  },

  async fetchReviewRequest(context, variables) {
    return queryItem('reviewRequest', reviewRequestQuery, variables)
  },

  async createReview(context, variables) {
    return mutateItem('createReview', createReviewMutation, variables)
  },

  async createQuestion(context, variables) {
    return mutateItem('createQuestion', createQuestionMutation, variables)
  },
}
